import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, ShieldAlert, Download } from 'lucide-react';

const PRODUCTION_DAYS = ['FRI', 'SAT', 'SUN', 'MON', 'TUES', 'WED', 'THURS'];

const WeeklySummaryTab = ({ selectedOrderId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (selectedOrderId) fetchSummary();
  }, [selectedOrderId]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/production/${selectedOrderId}/weekly-summary`);
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load weekly summary');
    } finally {
      setLoading(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/production/${selectedOrderId}/weekly-summary/export-pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Allergy_Summary_${data?.week_label || 'Week'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF exported');
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-gray-400">
        <ShieldAlert className="w-16 h-16 mx-auto mb-3 opacity-20" />
        <p className="text-sm">Select a week to view allergy summary</p>
      </div>
    );
  }

  return (
    <div data-testid="weekly-summary-tab">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>Weekly Allergy Summary</h2>
          <p className="text-sm text-gray-500 mt-0.5">{data.week_label}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2">
            <div className="text-[10px] text-red-500 uppercase tracking-wider font-bold">Total Allergy Meals</div>
            <div className="text-2xl font-bold text-red-700 tabular-nums" data-testid="total-allergy-count">{data.total_allergy_count}</div>
          </div>
          {data.total_allergy_count > 0 && (
            <Button onClick={exportPDF} disabled={exporting} className="btn-primary text-sm" data-testid="export-allergy-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Export PDF
            </Button>
          )}
        </div>
      </div>

      {data.total_allergy_count === 0 ? (
        <div className="text-center py-16 bg-emerald-50 border border-emerald-200 rounded-lg">
          <ShieldAlert className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
          <p className="text-sm font-medium text-emerald-700">No allergy notes recorded for this week</p>
          <p className="text-xs text-emerald-500 mt-1">All meals are standard — no dietary modifications required</p>
        </div>
      ) : (
        <div className="space-y-6">
          {PRODUCTION_DAYS.map(day => {
            const dayData = data.days.find(d => d.day === day);
            if (!dayData || dayData.entries.length === 0) return null;

            return (
              <div key={day} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`summary-day-${day}`}>
                {/* Day header */}
                <div className="flex items-center justify-between px-5 py-3 bg-red-50 border-b border-red-100">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-bold text-red-800">{day}</span>
                  </div>
                  <span className="text-xs font-bold text-red-600 bg-red-100 px-2.5 py-1 rounded-full">
                    {dayData.entries.length} allergy {dayData.entries.length === 1 ? 'meal' : 'meals'}
                  </span>
                </div>

                {/* Entries table */}
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Client</th>
                      <th>Key</th>
                      <th>Dish</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Pack Type</th>
                      <th>Allergy / Dietary Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayData.entries.map((entry) => (
                      <tr key={`${entry.client_name}-${entry.dish_name}-${entry.meal_size}`} className="bg-red-50/30">
                        <td className="font-medium text-sm">{entry.client_name}</td>
                        <td>
                          <span className="px-2 py-0.5 bg-gray-100 text-xs font-bold rounded">{entry.client_key}</span>
                        </td>
                        <td className="text-sm">{entry.dish_name}</td>
                        <td className="text-sm">{entry.meal_size}</td>
                        <td className="text-center tabular-nums font-bold">{entry.quantity}</td>
                        <td>
                          <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold rounded ${
                            entry.pack_type === 'Sharing' ? 'bg-blue-50 text-blue-700' :
                            entry.pack_type === 'Multi' ? 'bg-purple-50 text-purple-700' :
                            'bg-zinc-100 text-zinc-600'
                          }`}>{entry.pack_type || 'Individual'}</span>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            <span className="text-sm font-medium text-red-700">{entry.allergy_notes}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WeeklySummaryTab;
